import React, { useState, useEffect, useRef } from 'react';
import {
    Card,
    Typography,
    Spin,
    App,
    Alert,
    Button,
    Space
} from 'antd';
import {
    ReloadOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { deviceAuthService } from '../../services/posApi';
import { usePageTitle } from '../../context/PageTitleContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const DeviceLoginPage = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const { message } = App.useApp();
    const navigate = useNavigate();

    const [authCode, setAuthCode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState(null);
    const [expiresIn, setExpiresIn] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);

    // Refs для интервалов
    const checkStatusInterval = useRef(null);
    const countdownInterval = useRef(null);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Авторизация устройства');
        setRightContent(null);

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Проверяем, авторизовано ли уже устройство
    useEffect(() => {
        if (deviceAuthService.isAuthenticated()) {
            navigate('/device/sale');
            return;
        }

        // Проверяем, есть ли сохраненный код авторизации
        const savedCode = localStorage.getItem('device_auth_code');

        if (savedCode) {
            setAuthCode(savedCode);
            startPollingStatus(savedCode);
        } else {
            // Автоматически запрашиваем код при загрузке страницы
            requestAuthCode();
        }

        return () => {
            clearIntervals();
        };
    }, [navigate]);

    // Очистка интервалов
    const clearIntervals = () => {
        if (checkStatusInterval.current) {
            clearInterval(checkStatusInterval.current);
            checkStatusInterval.current = null;
        }
        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
        }
    };

    // Упрощенная функция проверки статуса (из рабочего примера)
    const startPollingStatus = (authCode) => {
        setChecking(true);

        if (checkStatusInterval.current) {
            clearInterval(checkStatusInterval.current);
        }

        checkStatusInterval.current = setInterval(async () => {
            try {
                const statusData = await deviceAuthService.checkAuthStatus(authCode);

                if (statusData.status === 'authorized') {
                    clearInterval(checkStatusInterval.current);
                    setChecking(false);
                    navigate('/device/sale');
                } else if (statusData.status === 'error') {
                    clearInterval(checkStatusInterval.current);
                    setChecking(false);
                    message.error('Ошибка авторизации');
                    localStorage.removeItem('device_auth_code');
                    setError('Ошибка авторизации');
                    // Запросить новый код через 2 секунды
                    setTimeout(() => {
                        requestAuthCode();
                    }, 2000);
                }
            } catch (e) {
                clearInterval(checkStatusInterval.current);
                setChecking(false);
                message.error(e.message);
                localStorage.removeItem('device_auth_code');
                setError(e.message || 'Ошибка соединения');
            }
        }, 5000); // Используем интервал 2 секунды как в рабочем примере
    };

    // Запрос кода авторизации
    const requestAuthCode = async () => {
        setLoading(true);
        setError(null);
        setAuthCode(null);
        clearIntervals();

        try {
            const response = await deviceAuthService.requestAuthCode();
            setAuthCode(response.auth_code);
            setExpiresIn(response.expires_in);
            setTimeLeft(response.expires_in);

            // Сохраняем код в localStorage
            localStorage.setItem('device_auth_code', response.auth_code);

            // Запускаем проверку статуса с упрощенной логикой
            startPollingStatus(response.auth_code);

            // Запускаем обратный отсчет
            startCountdown(response.expires_in);
        } catch (error) {
            setError(error.message);
            message.error(`Ошибка получения кода: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Запуск обратного отсчета
    const startCountdown = (seconds) => {
        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
        }

        countdownInterval.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearIntervals();
                    setChecking(false);
                    localStorage.removeItem('device_auth_code');
                    setError('Время действия кода истекло. Запросите новый код.');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Форматирование времени
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Если загрузка
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh'
            }}>
                <Space direction="vertical" align="center" size="large">
                    <Spin size="large" />
                    <Text>Получение кода авторизации...</Text>
                </Space>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh', // Вычитаем высоту AppHeader
                padding: '24px'
            }}
        >
            <Card
                variant="bordered"
                style={{
                    width: '100%',
                    maxWidth: 500,
                    textAlign: 'center'
                }}
            >
                {error ? (
                    <Space direction="vertical"  align="center" size="large" style={{ width: '100%' }}>
                        <Alert
                            message="Ошибка авторизации"
                            description={error}
                            type="error"
                            showIcon
                        />
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={requestAuthCode}
                            size="large"
                            loading={loading}
                        >
                            Получить новый код
                        </Button>
                    </Space>
                ) : authCode ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>

                        {/* Код авторизации */}
                        <div>
                            <Title
                                level={1}
                                style={{
                                    fontSize: '4rem',
                                    fontWeight: 'bold',
                                    margin: 0,
                                    letterSpacing: '0.2em',
                                }}
                            >
                                {authCode}
                            </Title>
                        </div>

                        {/* Инструкция */}
                        <div style={{ marginTop: 24 }}>
                            <Text style={{
                                fontSize: '16px',
                                lineHeight: '1.6'
                            }}>
                                В интерфейсе POS выберите <strong>Кассы</strong>, нажмите на нужную кассу,
                                затем нажмите на кнопку <strong>Авторизовать</strong> и введите числа.
                                <br /><br />
                                <strong>Не закрывайте окно, пока устройство не будет добавлено.</strong>
                            </Text>
                        </div>

                        {/* Кнопка обновления */}
                        <div style={{ marginTop: 32 }}>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={requestAuthCode}
                                disabled={checking}
                                loading={loading}
                            >
                                Получить новый код
                            </Button>
                        </div>
                    </Space>
                ) : null}
            </Card>
        </div>
    );
};

export default DeviceLoginPage;